import React, {useEffect, useMemo, useRef, useState} from 'react';
import {FormGroup, FormText, Input, Label} from 'reactstrap';
import {useIntl} from 'react-intl';
import {Input as UInput} from '../../components/input';
import {Form} from '@unform/web';
import * as Yup from 'yup';
import './device.sass';
import {GroupButtonForm} from '../../components/group-button-form';
import {FieldSet} from '../../components/fieldset';
import useFormatter from '../../hooks/user-formatter';
import useAlerts from '../../hooks/use-alerts';
import useTimezone from '../../hooks/use-timezone';
import DeviceService from '../../services/device-service';
import {formatDateTimeColumn} from '../../helpers/excel-helper';
import {
    HOURS_MAX_MS,
    ODOMETER_MAX_METERS,
    hoursRolledOver,
    odometerRolledOver,
    parseDecimal,
    roundOneDecimal,
    wrapHours,
    wrapOdometer,
} from '../../helpers/accumulator';
import AccumulatorHistoryTable from './accumulator-history-table';

const UNIT_LABELS = {km: 'accumulatorUnitKm', mi: 'accumulatorUnitMi', nmi: 'accumulatorUnitNmi'};
const ONE_DECIMAL = {minimumFractionDigits: 1, maximumFractionDigits: 1};

const AccumulatorForm = (props) => {
    const {
        data,
        loading,
        cancel,
        saveAccumulator,
    } = props;

    const intl = useIntl();
    const {toast} = useAlerts();
    const {timezone} = useTimezone();
    const formRef = useRef(null);
    const {distanceConverter, hoursConverter, getAttributePreference} = useFormatter();

    const unit = getAttributePreference('distanceUnit', 'km');
    const unitLabel = intl.formatMessage({id: UNIT_LABELS[unit] || UNIT_LABELS.km});

    const attributes = data.currentPosition && data.currentPosition.attributes;
    const storedDistance = (attributes && attributes.totalDistance) || 0;
    const storedHours = (attributes && attributes.hours) || 0;

    // Valor mostrado: na unidade do usuario, com uma casa, e reiniciado em zero depois do maximo, como um painel.
    // Fixo desde a abertura, como os campos: uma posicao nova com o formulario aberto (horimetro andando com o motor
    // ligado) nao pode fazer o campo intocado parecer editado e mandar o valor antigo ao servidor.
    const [initial] = useState(() => ({
        deviceId: data.id,
        totalDistance: roundOneDecimal(distanceConverter(wrapOdometer(storedDistance))),
        hours: roundOneDecimal(hoursConverter(wrapHours(storedHours))),
    }));

    // Depois da virada o campo mostra o valor reiniciado, mas o servidor guarda o total e recusa valor acima do
    // maximo: qualquer valor digitado seria recusado ou faria o contador voltar. O campo fica so para leitura.
    const distanceLocked = odometerRolledOver(storedDistance);
    const hoursLocked = hoursRolledOver(storedHours);

    const maxDistance = roundOneDecimal(distanceConverter(ODOMETER_MAX_METERS));
    const maxHours = roundOneDecimal(hoursConverter(HOURS_MAX_MS));

    const rangeMessage = (max, suffix) => intl.formatMessage(
        {id: 'accumulatorRangeError'}, {max: `${intl.formatNumber(max, ONE_DECIMAL)}${suffix}`});
    const decimalMessage = intl.formatMessage({id: 'accumulatorDecimalError'});

    // Espelho das regras do servidor, so para responder rapido; quem decide e o servidor.
    const counter = (max, suffix) => {
        const message = rangeMessage(max, suffix);
        return Yup.number()
            // Campo vazio nao vira zero: um odometro editado sem querer para 0 seria uma reducao.
            .transform((value, original) => (String(original ?? '').trim() === '' ? NaN : parseDecimal(original)))
            .typeError(message)
            .min(0, message)
            .max(max, message)
            .test('one-decimal', decimalMessage, (value) => value === undefined ||
                Math.abs(value * 10 - Math.round(value * 10)) < 1e-9);
    };

    const schema = Yup.object().shape({
        deviceId: Yup.number(),
        totalDistance: counter(maxDistance, ` ${unitLabel}`),
        hours: counter(maxHours, ' h'),
    });

    const [entered, setEntered] = useState({
        totalDistance: String(initial.totalDistance), hours: String(initial.hours)});

    const handleF8Key = () => {
        if (!submitDisabled) {
            formRef.current.submitForm();
        }
    };

    const handleSubmit = async (formData) => {
        // Enter dentro do campo envia o formulario mesmo com o botao indisponivel.
        if (submitDisabled) {
            return;
        }
        try {
            const values = await schema.validate(formData, {abortEarly: false});

            // Vai so o que o usuario mudou: o resto continua como o servidor guarda, sem risco de o arredondamento
            // da tela virar uma edicao.
            const payload = {deviceId: data.id};
            if (!distanceLocked && values.totalDistance !== initial.totalDistance) {
                payload.totalDistance = Math.round(distanceConverter(values.totalDistance, undefined, true));
            }
            if (!hoursLocked && values.hours !== initial.hours) {
                payload.hours = Math.round(hoursConverter(values.hours, undefined, true));
            }
            if (payload.totalDistance === undefined && payload.hours === undefined) {
                toast.info(intl.formatMessage({id: 'accumulatorNothingChanged'}));
                return;
            }
            saveAccumulator(payload);
        } catch (err) {
            if (err instanceof Yup.ValidationError) {
                const validationErrors = {};
                err.inner.forEach((error) => {
                    validationErrors[error.path] = error.message;
                });
                formRef.current.setErrors(validationErrors);
            } else {
                throw err;
            }
        }
    };

    const [history, setHistory] = useState(
        {loading: true, error: false, registeredAt: null, rows: [], distanceFloor: null, hoursFloor: null});
    useEffect(() => {
        let active = true;
        new DeviceService().accumulatorHistory(data.id)
            .then((result) => active && setHistory({
                loading: false,
                error: false,
                registeredAt: result.registeredAt,
                rows: result.rows || [],
                distanceFloor: result.distanceFloor ?? null,
                hoursFloor: result.hoursFloor ?? null,
            }))
            .catch(() => active && setHistory({
                loading: false, error: true, registeredAt: null, rows: [], distanceFloor: null, hoursFloor: null}));
        return () => {
            active = false;
        };
    }, [data.id]);

    // Depois da primeira edicao de um campo, o servidor so aceita valor igual ou maior que o maior entre o valor
    // atual e o ultimo registrado (o piso). Antes, qualquer valor. A tela avisa e bloqueia o salvar; o servidor
    // continua decidindo.
    const floorDisplay = (floor, stored, convert) => (floor === null ? null : roundOneDecimal(convert(Math.max(stored, floor))));
    const minDistance = distanceLocked ? null : floorDisplay(history.distanceFloor, storedDistance, distanceConverter);
    const minHours = hoursLocked ? null : floorDisplay(history.hoursFloor, storedHours, hoursConverter);
    const belowFloor = (text, initialValue, min) => {
        const value = parseDecimal(text);
        return min !== null && Number.isFinite(value) && value !== initialValue && value < min;
    };
    const distanceBlocked = belowFloor(entered.totalDistance, initial.totalDistance, minDistance);
    const hoursBlocked = belowFloor(entered.hours, initial.hours, minHours);
    const submitDisabled = distanceBlocked || hoursBlocked;
    const lowerMessage = (min, suffix) => intl.formatMessage(
        {id: 'accumulatorLowerBlocked'}, {min: `${intl.formatNumber(min, ONE_DECIMAL)}${suffix}`});

    const formatDistance = (meters) => `${intl.formatNumber(
        roundOneDecimal(distanceConverter(wrapOdometer(meters))), ONE_DECIMAL)} ${unitLabel}`;
    const formatHours = (ms) => `${intl.formatNumber(
        roundOneDecimal(hoursConverter(wrapHours(ms))), ONE_DECIMAL)} h`;
    const formatDate = (date) => (date ? formatDateTimeColumn(date, timezone) : '—');
    const neverEdited = intl.formatMessage({id: 'accumulatorNeverEdited'});
    const FIELDS = [
        {kind: 'odometer', label: intl.formatMessage({id: 'accumulatorFieldOdometer'}), format: formatDistance},
        {kind: 'hours', label: intl.formatMessage({id: 'accumulatorFieldHourmeter'}), format: formatHours},
    ];
    // Uma linha por edicao. Campo que nunca foi editado ganha uma linha com a data de cadastro e "Nunca editado".
    const rows = useMemo(() => {
        const registeredAt = formatDate(history.registeredAt);
        const edits = history.rows.map((row, index) => {
            const field = FIELDS.find((item) => item.kind === row.kind) || FIELDS[0];
            return {
                key: `edit-${index}`,
                registeredAt,
                field: field.label,
                previous: field.format(row.previousValue),
                editedAt: formatDate(row.editedAt),
                edited: field.format(row.newValue),
                user: row.userName || '—',
                email: row.userEmail || '—',
            };
        });
        const untouched = FIELDS.filter((field) => !history.rows.some((row) => row.kind === field.kind))
            .map((field) => ({
                key: `never-${field.kind}`,
                registeredAt,
                field: field.label,
                previous: neverEdited,
                editedAt: neverEdited,
                edited: neverEdited,
                user: '—',
                email: '—',
            }));
        return [...edits, ...untouched];
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [history, unit, timezone, intl]);

    return (
        <>
            <Form
                ref={formRef}
                className="accumulator-form"
                onSubmit={handleSubmit}
                initialData={initial}>
                <UInput hidden type="number" name="deviceId"/>
                <FieldSet className="text-left border p-4">
                    <div>
                        <FormText color="danger" className="d-block mb-3">
                            {intl.formatMessage({id: 'accumulatorStoppedNotice'})}
                        </FormText>
                        <FormText className="d-block mb-3">
                            {intl.formatMessage({id: 'accumulatorPlatformOdometerNotice'})}
                        </FormText>
                        {!attributes && (
                            <FormText color="danger" className="d-block mb-3">
                                {intl.formatMessage({id: 'accumulatorNoPosition'})}
                            </FormText>
                        )}
                        <div className="row">
                            <div className={'col'}>
                                <FormGroup className="custom-formGroup">
                                    <Label for="accumulatorDistance">
                                        {intl.formatMessage({id: 'accumulatorCurrentOdometer'}, {unit: unitLabel})}
                                    </Label>
                                    <Input
                                        tag={UInput}
                                        type="text"
                                        name="totalDistance"
                                        id="accumulatorDistance"
                                        disabled={distanceLocked}
                                        onChange={(event) => {
                                            // O valor sai do evento antes da atualizacao: o React 16 libera o
                                            // evento ao fim do manipulador e a funcao roda depois disso.
                                            const {value} = event.target;
                                            setEntered((current) => ({...current, totalDistance: value}));
                                        }}
                                    />
                                    {distanceBlocked && (
                                        <FormText color="danger" className="d-block">
                                            {lowerMessage(minDistance, ` ${unitLabel}`)}
                                        </FormText>
                                    )}
                                    {distanceLocked && (
                                        <FormText color="danger" className="d-block">
                                            {intl.formatMessage({id: 'accumulatorRolledOver'})}
                                        </FormText>
                                    )}
                                </FormGroup>

                                <FormGroup className="custom-formGroup">
                                    <Label for="accumulatorHours">
                                        {intl.formatMessage({id: 'accumulatorCurrentHourmeter'})}
                                    </Label>
                                    <Input
                                        tag={UInput}
                                        type="text"
                                        name="hours"
                                        id="accumulatorHours"
                                        disabled={hoursLocked}
                                        onChange={(event) => {
                                            const {value} = event.target;
                                            setEntered((current) => ({...current, hours: value}));
                                        }}
                                    />
                                    {hoursBlocked && (
                                        <FormText color="danger" className="d-block">
                                            {lowerMessage(minHours, ' h')}
                                        </FormText>
                                    )}
                                    {hoursLocked && (
                                        <FormText color="danger" className="d-block">
                                            {intl.formatMessage({id: 'accumulatorRolledOver'})}
                                        </FormText>
                                    )}
                                </FormGroup>
                            </div>
                        </div>
                    </div>
                </FieldSet>
                <GroupButtonForm
                    loading={loading}
                    submitDisabled={submitDisabled}
                    cancel={cancel}
                    submit={handleF8Key}
                />
            </Form>
            <AccumulatorHistoryTable loading={history.loading} error={history.error} rows={rows} />
        </>
    );
};

export default AccumulatorForm;
